
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Loader2, PenLine, Eye } from "lucide-react";
import BlogPostForm from "@/components/blog-management/BlogPostForm";
import EnhancedBlogPreview from "@/components/blog-management/EnhancedBlogPreview";
import { useBlogPost } from "@/hooks/useBlogPost";
import { BlogHeader } from "@/components/blog-management/editor/BlogHeader";
import { useAutosave } from "@/hooks/editor/useAutosave";
import { useBlogPostForm } from "@/hooks/useBlogPostForm";
import { BlogPostFormData } from "@/types/blog";

const BlogPostEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const [activeTab, setActiveTab] = useState<string>("edit");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Only fetch post data if we're editing an existing post
  const { data: postData, isLoading } = useBlogPost(
    isNew ? "" : id || "", 
    "id",
    { enabled: !isNew } // Only run the query if not creating a new post
  );
  
  const {
    formData,
    setFormData,
    isSubmitting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleFormChange,
    handleSave,
    handleDelete,
    promptIfUnsaved
  } = useBlogPostForm({
    id: id || "new",
    isNew
  });

  // Setup autosave
  const { saveToLocalStorage, clearLocalStorage, loadFromLocalStorage } = useAutosave({
    id: id || "new",
    formData,
    hasUnsavedChanges,
    setLastSaved,
    setIsSaving
  });

  // Load initial data when post data is fetched
  useEffect(() => {
    if (!isNew && postData && !isLoading) {
      // Normalize metadata to ensure expected structure
      const normalizedMetadata = {
        author: postData.metadata?.author || "",
        categories: Array.isArray(postData.metadata?.categories) 
          ? postData.metadata.categories 
          : [],
        tags: Array.isArray(postData.metadata?.tags) 
          ? postData.metadata.tags 
          : [],
        featured: Boolean(postData.metadata?.featured),
        seo_description: postData.metadata?.seo_description || "",
        sources: Array.isArray(postData.metadata?.sources)
          ? postData.metadata.sources
          : [],
        ...postData.metadata // Preserve any other metadata fields
      };

      setFormData({
        title: postData.title || "",
        content: postData.content || "",
        excerpt: postData.excerpt || "",
        image_url: postData.image_url || "",
        slug: postData.slug || "",
        status: postData.status || "draft",
        scheduled_for: postData.scheduled_for ? new Date(postData.scheduled_for) : null,
        published_at: postData.published_at ? new Date(postData.published_at) : null,
        metadata: normalizedMetadata
      });

      // Reset unsaved changes flag when loading new data
      setHasUnsavedChanges(false);
    }
  }, [isNew, postData, isLoading, setFormData]);

  // Try to load from localStorage on initial mount
  useEffect(() => {
    if (!id) return;
    
    const savedData = loadFromLocalStorage();
    if (savedData) {
      setFormData(savedData as BlogPostFormData);
      setHasUnsavedChanges(true);
    }
  }, [id, loadFromLocalStorage, setFormData]);

  // Before tab change, save current state
  const handleTabChange = (newTab: string) => {
    // Save current state if we're leaving the edit tab
    if (activeTab === "edit" && newTab === "preview") {
      saveToLocalStorage();
    }
    
    setActiveTab(newTab);
  };

  const handleSuccessfulSave = async () => {
    const success = await handleSave();
    if (success) {
      // Clear localStorage after successful save
      clearLocalStorage();
    }
  };

  // Only show loading state when actually fetching post data (not for new posts)
  const isLoaded = isNew || !isLoading;

  return (
    <div className="space-y-6">
      <BlogHeader
        isNew={isNew}
        formData={formData}
        isSubmitting={isSubmitting}
        isLoaded={isLoaded}
        activeTab={activeTab}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaved={lastSaved}
        isSaving={isSaving}
        onTabChange={handleTabChange}
        onDelete={handleDelete}
        onSave={handleSuccessfulSave}
        promptIfUnsaved={promptIfUnsaved}
      />

      {!isLoaded ? (
        <Card className="p-8 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <Loader2 size={24} className="animate-spin text-slate-600 mb-2" />
            <p className="text-slate-600">Loading post data...</p>
          </div>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <Card>
            <div className="p-1">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="edit">
                  <PenLine className="mr-2 h-4 w-4" />
                  Content Editor
                </TabsTrigger>
                <TabsTrigger value="preview">
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </TabsTrigger>
              </TabsList>
            </div>
            <Separator />
            <TabsContent value="edit" className="p-4 pt-6">
              <BlogPostForm
                formData={formData}
                onChange={handleFormChange}
                isLoading={isSubmitting}
              />
            </TabsContent>
            <TabsContent value="preview" className="p-4 overflow-auto">
              <EnhancedBlogPreview post={formData} />
            </TabsContent>
          </Card>
        </Tabs>
      )}
    </div>
  );
};

export default BlogPostEditor;
